import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatCard } from '@angular/material/card';
import { Router } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { BehaviorSubject, filter, take } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { Logger } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { getColor, ColorProp } from '../../../layout/Colors';
import { DataTableColumn } from '../../../shared/components/data-table/DataTableColumn.model';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { SearchService } from '../../../shared/search/search.service';
import { FilterParams, SortParams } from '../../../shared/search/SearchParams';
import { Policy, PolicyRow } from '../models/Policy';
import { PoliciesFilterStateService } from '../services/policies-filter-state.service';
import { PoliciesTableMessageService } from '../services/policies-table-message.service';
import { PolicyService } from '../services/policy.service';
import { PoliciesFilterStatusComponent } from './components/filters/policies-filter-status/policies-filter-status.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DataTableFilterPillComponent, DataTableFilterPillState } from '../../../shared/components/data-table/filters/components/data-table-filter-pill/data-table-filter-pill.component';
import { ActiveEventMessage, DataTableFilterMessageService } from '../../../shared/components/data-table/filters/components/data-table-filter-message-service';
import { PillComponent } from '../../../shared/components/pill/pill.component';

@Component({
  selector: 'app-policies-table',
  templateUrl: './policies-table.component.html',
  styleUrls: ['./policies-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIcon,
    MatCard,
    MatTableModule,
    MatFormFieldModule,
    MatSortModule,
    MatPaginatorModule,
    MatInputModule,
    FormsModule,
    MatProgressSpinner,
    DataTableFilterPillComponent,
    PillComponent
]
})
export class PoliciesTableComponent implements OnInit, AfterViewInit {

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  STATUS_ACTIVE = 1;
  STATUS_INACTIVE = 2;

  activeFilter: boolean = false;
  inactiveFilter: boolean = false;

  displayedColumns: DataTableColumn[] = [
    { column: 'id', display: 'ID' },
    { column: 'name', display: 'Name' },
    { column: 'contentsCoverage', display: 'Contents Coverage' },
    { column: 'hoLiabilityCoverage', display: 'Liability Coverage' },
    { column: 'dwellingCoverage', display: 'Dwelling Coverage' },
    { column: 'pillStatus', display: 'Status' }
  ]
  displayedColumnNames: string[] = [];
  pageNumber: number = 1;
  searchKeyword: string = '';
  filterPills = new Map<string, DataTableFilterPillState>();

  filterDefinitions: DataTableFilter[] = [
    { id: uuid(), name: 'Status', shortName: 'Status', component: PoliciesFilterStatusComponent }
  ];

  loading = new BehaviorSubject<boolean>(false);
  policies: Policy[] = [];
  mappedPolicies: PolicyRow[] = [];
  dataSource = new MatTableDataSource<PolicyRow>;
  tableLength = 0;
  pageSize = 10;
  keyword: string = '';
  createPolicyByComponentEnabled: boolean = true;

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    private router: Router,
    private policyService: PolicyService,
    private searchService: SearchService,
    private policiesFilterStateService: PoliciesFilterStateService,
    private policiesMessageService: PoliciesTableMessageService,
    public userContextProvider: UserContextProvider,
    private logService: Logger,
    public snackbar: MatSnackBar,
    private filterMessageService: DataTableFilterMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.createPolicyByComponentEnabled = true;
      this.setupAfterContextLoaded();
      this.subscribeToFilterActiveEvent();
      this.initTableColumns();
      this.loadPage();
    });

    const storedItemsPerPage = localStorage.getItem('policiesItemsPerPage');
    if (storedItemsPerPage) {
      this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
    } else {
      this.initialPageSize.next(10);
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;   

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  initTableColumns(): void {
    if (this.displayedColumns === null) {
      this.displayedColumns = [];
    }
    this.displayedColumnNames = this.displayedColumns.map(c => c.column);
  }

  applySearch(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onFilterPillCloseEvent(filterId: string) {
    this.filterMessageService.onClearClicked(filterId);
  }

  setupAfterContextLoaded() {
    this.setupFilterEventHandler();
  }

  setupFilterEventHandler() {
    this.policiesMessageService.applyFilterEvent.subscribe(_ => {
      const status = this.policiesFilterStateService.status;
      this.handleStatusFilterUpdate(Number(status));
    });
  }

  handleStatusFilterUpdate(status: number) {
    switch(status) {
      case 1:
        this.activeFilter = true;
        this.inactiveFilter = false;
        this.updateTableDataSource(this.mappedPolicies.filter(policy => policy.statusId === 1));
        this.setPagination();
        break;
      case 2:
        this.activeFilter = false;
        this.inactiveFilter = true;
        this.updateTableDataSource(this.mappedPolicies.filter(policy => policy.statusId === 2));
        this.setPagination();
        break;
      default:
        this.activeFilter = false;
        this.inactiveFilter = false;
        this.dataSource.data = this.mappedPolicies;
        this.tableLength = this.mappedPolicies.length;
        this.setPagination();
        break;
    }
  }

  setPagination() {
    this.dataSource = new MatTableDataSource(this.dataSource.data.slice(0, 10));
  }

  async loadPage() {
    try {
      this.loading.next(true);
      this.dataSource.data = [];

      this.policies = await this.policyService.getPolicies();
      this.mapPolicyDatasource(this.policies);
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Policies', 'Error while retrieving Policies');
    } finally {
      this.loading.next(false);
    }
  }

  async mapPolicyDatasource(policies: Policy[]) {
    this.dataSource.data = [];
    this.mappedPolicies = policies.map((policy: any) => this.buildPolicyTableRow(policy));
    this.updateTableDataSource(this.mappedPolicies);
  }

  buildPolicyTableRow(policy: any): PolicyRow {
    return {
      id: policy.id,
      name: policy.name,
      contentsCoverage: Number(policy.damage_coverage),
      hoLiabilityCoverage: Number(policy.liability_coverage),
      dwellingCoverage: Number(policy.dwelling_coverage),
      pillStatus: policy.status.display_text,
      statusId: policy.status_id
    }
  }

  updateTableDataSource(mappedPolicies: PolicyRow[]) {
    this.dataSource.data = mappedPolicies;
    this.tableLength = mappedPolicies.length;
  }

  updateStatusFilter(status: string) {
    const filter: FilterParams = {
      fieldName: 'status',
      value: [status],
      range: false
    };
    this.searchService.replaceFilter(filter);
  }

  onNewPolicyClick(): void {
    this.router.navigate(['/policies/form']);
  }

  onSortEvent(sortParams: SortParams) {
    if (sortParams.sortField === 'pillStatus') {
      sortParams.sortField = 'status';
    }
    this.searchService.addSort(sortParams);
    this.loadPage();
  }

  onPageEvent(event: PageEvent): void {
    this.pageNumber = event.pageIndex + 1;
    localStorage.setItem('policiesItemsPerPage', event.pageSize.toString());
  }

  onSearchEvent(keyword: string): void {
    this.dataSource.filter = keyword.trim().toLowerCase();
  }

  getPolicyStatusColor(status: string): string {
    if (status === "Active"){
      return getColor(ColorProp.PropertyStatusActive);
    } else {
      return '#d3d3d3';
    }
  }

  resetSearchQuery() {
    this.searchService.resetQuery();
    this.searchService.setSort("id", -1);
  }

  onDeleteKeyUp() {
    if (this.searchKeyword == '') {
      this.clearSearch();
    }
  }

  clearSearch() {
    this.searchKeyword = '';
    this.dataSource = new MatTableDataSource(this.mappedPolicies);
    this.ngAfterViewInit();
  }

  onGoToPageClick() {
    const max = Math.trunc(Math.ceil(this.paginator.length / this.paginator.pageSize));
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    this.pageNumber = pageNumber;

    if (this.paginator) {
      if (goToFirstPage) {
        this.paginator.firstPage();
      } else {
        this.paginator.lastPage();
      }
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.snackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  subscribeToFilterActiveEvent() {
    this.filterMessageService.activeEvent.subscribe((event: ActiveEventMessage) => {
      if (!this.filterDefinitions) {
        return;
      }

      this.resetPaginator(1, true);
      if (event.active) {
        let statusId: number = 0;
        let pillText = this.filterDefinitions.find(definition => definition.id == event.filterId)?.shortName || '';

        if (event.value) {
          pillText = pillText + " : " + event.value;
          if (event.value === "Inactive") {
            statusId = 2;
          } else {
            statusId = 1;
          }
        }
        // TODO: Is this handleStatus call needed once filter implemented?
        this.handleStatusFilterUpdate(statusId);
        this.filterPills.set(event.filterId, { filterId: event.filterId, text: pillText });
      } else {
        this.filterPills.delete(event.filterId);
      }
      this.cdr.detectChanges();
    });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/policies/detail/${row['id']}`]);
  }
}